/**
 * Roční přehledy: za včelstvo (s porovnáním let) a za stanoviště.
 * „Rok“ se řídí Nastavením – kalendářní, nebo včelařský od zvoleného měsíce.
 */
import { useState, type ReactNode } from 'react';
import { queenColor, type Colony, type ColonyEvent } from '../../db/model';
import { read } from '../../db/db';
import { fmt, naturalLabel } from '../../db/queries';
import { todayISO } from '../../db/rules';
import { season } from '../../db/season';
import { seasonsWithData, trend, yearStats, type YearStats } from '../../db/yearly';
import { seasonCalendar, varroaSeries } from '../../db/reports';
import { ExportButtons } from '../export';
import { CalendarLegend, SeasonCalendar, VarroaChart, VarroaLegend, niceMax } from '../charts';
import { useQuery, useRoute, useSeasonStart } from '../hooks';
import { describeEvent } from '../describe';
import { colonyName, COLONY_STATUS, EVENT_LABEL, FEED_TYPE, HONEY_TYPE, QUEEN_COLOR, fmtDate, plural } from '../labels';
import { Chips, Empty, Link, Notice, Screen, Section } from '../ui';

const n = (v: number | null | undefined, digits = 1) => (v == null ? '–' : fmt(v, digits));

function SeasonPicker({ years, value, startMonth, onChange }: { years: number[]; value: number; startMonth: number; onChange: (y: number) => void }) {
  return (
    <div className="no-print">
      <Chips ariaLabel="Rok" options={years.map((y) => [y, season(y, startMonth).label] as [number, string])} value={value} onChange={(v) => v && onChange(v)} />
    </div>
  );
}

function feedText(s: Pick<YearStats, 'feedSugarKg' | 'feedings'>) {
  return s.feedings.length ? `${fmt(s.feedSugarKg)} kg cukru` : '–';
}

function varroaText(s: YearStats) {
  if (s.totalMites != null) return plural(s.totalMites, 'roztoč', 'roztoči', 'roztočů');
  if (s.maxInfestation != null) return `${fmt(s.maxInfestation)} %`;
  return '–';
}

// ---------------------------------------------------------------------------
// Roční přehled včelstva
// ---------------------------------------------------------------------------

export function ColonyYearScreen({ id }: { id: string }) {
  const { query } = useRoute();
  const startMonth = useSeasonStart();
  const [picked, setPicked] = useState<number | null>(query.has('r') ? Number(query.get('r')) : null);
  const data = useQuery(async () => {
    const [colony, apiaries, events, queens, colonies] = await Promise.all([
      read.get('colonies', id),
      read.all('apiaries'),
      read.byIndex('events', 'colonyIds', id),
      read.all('queens'),
      read.all('colonies'),
    ]);
    return { colony, apiaries, events, queens, colonies };
  }, [id]);
  if (!data?.colony || startMonth == null) return null;
  const { colony, apiaries, events, queens } = data;
  const years = seasonsWithData(events, startMonth, todayISO());
  const year = picked ?? years[0];
  const stats = years.map((y) => yearStats(colony, events, queens, season(y, startMonth)));
  const st = stats[years.indexOf(year)] ?? yearStats(colony, events, queens, season(year, startMonth));
  const apiaryName = apiaries.find((a) => a.id === colony.apiaryId)?.name;

  return (
    <Screen
      title={`Rok ${st.season.label}`}
      crumbs={[
        [apiaryName ?? 'Stanoviště', `stanoviste/${colony.apiaryId}`],
        [colonyName(colony.label), `vcelstvo/${id}`],
      ]}
      backTo={`vcelstvo/${id}`}
    >
      <SeasonPicker years={years} value={year} startMonth={startMonth} onChange={setPicked} />
      <div className="print-only print-head">
        <h1>
          Včelstvo {colonyName(colony.label)} – {apiaryName} – rok {st.season.label}
        </h1>
        <p>
          {fmtDate(st.season.from)} – {fmtDate(st.season.to)}
        </p>
      </div>
      <p className="small muted">
        {fmtDate(st.season.from)} – {fmtDate(st.season.to)} · {plural(st.events, 'zápis', 'zápisy', 'zápisů')}
      </p>

      <div className="info-grid">
        <Stat label="Med" value={`${fmt(st.honeyKg)} kg`} sub={st.harvests.length ? `${st.harvests.length}× medobraní` : 'bez medobraní'} />
        <Stat label="Krmení" value={feedText(st)} sub={st.feedings.length ? `${st.feedings.length}× krmeno` : 'nekrmeno'} />
        <Stat label="Léčení" value={st.treatments.length ? `${st.treatments.length}×` : '–'} sub={st.treatments.at(-1) ? `naposledy ${fmtDate(st.treatments.at(-1)!.date)}` : 'neléčeno'} />
        <Stat label="Varroáza – celkový spad" value={varroaText(st)} sub={plural(st.varroaChecks.length, 'kontrola', 'kontroly', 'kontrol')} alarm={(st.maxMitesPerDay ?? 0) >= 10 || (st.maxInfestation ?? 0) >= 3} />
        <Stat
          label="Síla"
          value={st.maxSeams != null ? `${st.maxSeams} uliček` : '–'}
          sub={[plural(st.inspections, 'prohlídka', 'prohlídky', 'prohlídek'), st.avgTemperament != null ? `mírnost ${fmt(st.avgTemperament)}/5` : null].filter(Boolean).join(' · ')}
        />
        <Stat label="Stav na konci" value={COLONY_STATUS[st.endStatus]} sub={st.swarms ? `${st.swarms}× rojení` : st.swarmCells ? 'rojové matečníky' : 'bez rojení'} />
      </div>
      {st.diseaseSuspicion && <Notice tone="warn">V tomto roce bylo při prohlídce zapsáno podezření na nemoc.</Notice>}

      <VarroaSection colonyId={colony.id} events={events} from={st.season.from} to={st.season.to} />

      <Section title="Matka">
        {st.queens.length === 0 ? (
          <p className="muted">Matka v tomto roce neevidována.</p>
        ) : (
          <ul className="plain">
            {st.queens.map((q, i) => (
              <li key={i}>
                <span className="queen-dot" style={{ background: QUEEN_COLOR[queenColor(q)]?.css }} /> rok {q.year}
                {q.line ? `, ${q.line}` : ''}
                {q.marked ? ', značená' : ''}
              </li>
            ))}
          </ul>
        )}
        {(st.queenChanges > 0 || st.splits > 0) && (
          <p className="small muted">
            {[st.queenChanges ? `${st.queenChanges}× výměna matky` : null, st.splits ? `${st.splits}× oddělek` : null].filter(Boolean).join(' · ')}
          </p>
        )}
      </Section>

      <Detail title="Medobraní" empty="Bez medobraní.">
        {st.harvests.map((h, i) => (
          <Row key={i} date={h.date}>
            {fmt(h.kg)} kg · {HONEY_TYPE[h.honeyType as keyof typeof HONEY_TYPE]?.toLowerCase()}
            {h.waterPercent != null ? ` · voda ${fmt(h.waterPercent)} %` : ''}
          </Row>
        ))}
      </Detail>
      {(st.supersAdded > 0 || st.supersRemoved > 0) && (
        <p className="small muted">
          Medníky: nasazeno {st.supersAdded}× {st.firstSuperDate ? `(poprvé ${fmtDate(st.firstSuperDate)})` : ''}, odebráno {st.supersRemoved}×
          {st.lastSuperRemovedDate ? ` (naposledy ${fmtDate(st.lastSuperRemovedDate)})` : ''}
        </p>
      )}
      <Detail title="Krmení" empty="Bez krmení.">
        {st.feedings.map((f, i) => (
          <Row key={i} date={f.date}>
            {FEED_TYPE[f.feedType as keyof typeof FEED_TYPE]}, {fmt(f.amount)} {f.unit}
            {` (≈ ${fmt(f.sugarKg)} kg cukru)`}
          </Row>
        ))}
      </Detail>
      <Detail title="Léčení" empty="Bez léčení.">
        {st.treatments.map((t, i) => (
          <Row key={i} date={t.date}>
            {t.product}
            {t.batch ? ` · šarže ${t.batch}` : ''}
          </Row>
        ))}
      </Detail>
      <Detail title="Kontroly varroázy" empty="Bez kontrol.">
        {st.varroaChecks.map((v, i) => (
          <Row key={i} date={v.date}>
            {plural(v.miteCount, 'roztoč', 'roztoči', 'roztočů')}
            {v.percent != null ? ` · ${fmt(v.percent)} %` : ''}
          </Row>
        ))}
      </Detail>
      {st.sentToLab && <p className="small muted">Zimní měl odeslána k vyšetření.</p>}

      <Comparison stats={stats} current={year} />

      <ExportButtons
        table={{
          name: `Včelstvo ${colonyName(colony.label)} – rok ${st.season.label}`,
          head: ['Datum', 'Zápis', 'Podrobnosti', 'Poznámka'],
          rows: events
            .filter((e) => !e.deletedAt && e.date >= st.season.from && e.date <= st.season.to)
            .sort((a, b) => a.date.localeCompare(b.date))
            .map((e) => [
              fmtDate(e.date),
              EVENT_LABEL[e.type],
              describeEvent(e, {
                apiary: (aid) => apiaries.find((a) => a.id === aid)?.name ?? '',
                colony: (cid) => colonyName(data.colonies.find((c) => c.id === cid)?.label) ?? '',
              }),
              e.note,
            ]),
        }}
      />
    </Screen>
  );
}

function Stat({ label, value, sub, alarm }: { label: string; value: string; sub?: string; alarm?: boolean }) {
  return (
    <div className="info">
      <div className="info-label">{label}</div>
      <div className={'info-value' + (alarm ? ' alarm' : '')}>
        {value}
        {sub && <div className="muted small">{sub}</div>}
      </div>
    </div>
  );
}

function Detail({ title, empty, children }: { title: string; empty: string; children: ReactNode[] }) {
  return (
    <Section title={title}>
      {children.length === 0 ? <p className="muted small">{empty}</p> : <ul className="plain rows">{children}</ul>}
    </Section>
  );
}

function Row({ date, children }: { date: string; children: ReactNode }) {
  return (
    <li>
      <span className="row-date">{fmtDate(date)}</span>
      <span>{children}</span>
    </li>
  );
}

/** Porovnání s předchozími roky: tabulka ukazatelů a graf medu. */
function Comparison({ stats, current }: { stats: YearStats[]; current: number }) {
  // nejvýš 5 let, od nejstaršího vlevo
  const shown = stats.filter((s) => s.season.year <= current).slice(0, 5).reverse();
  if (shown.length < 2) {
    return (
      <Section title="Porovnání s předchozími roky">
        <p className="muted small">Porovnání se ukáže, až budou zápisy alespoň za dva roky.</p>
      </Section>
    );
  }
  const rows: [string, (s: YearStats) => number | null, (v: number | null) => string][] = [
    ['Med (kg)', (s) => s.honeyKg, (v) => n(v)],
    ['Medobraní (×)', (s) => s.harvests.length, (v) => n(v, 0)],
    ['Krmení – cukr (kg)', (s) => s.feedSugarKg, (v) => n(v)],
    ['Léčení (×)', (s) => s.treatments.length, (v) => n(v, 0)],
    ['Celkový spad roztočů', (s) => s.totalMites, (v) => n(v, 0)],
    ['Nejvíc uliček', (s) => s.maxSeams, (v) => n(v, 0)],
    ['Rojení (×)', (s) => s.swarms, (v) => n(v, 0)],
  ];
  const maxHoney = Math.max(...shown.map((s) => s.honeyKg), 1);
  return (
    <Section title="Porovnání s předchozími roky">
      <div className="table-wrap">
        <table className="log compare">
          <thead>
            <tr>
              <th />
              {shown.map((s) => (
                <th key={s.season.year} className={s.season.year === current ? 'cur' : ''}>
                  {s.season.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, get, show]) => (
              <tr key={label}>
                <th scope="row">{label}</th>
                {shown.map((s, i) => {
                  const v = get(s);
                  const t = i > 0 ? trend(v, get(shown[i - 1])) : '';
                  return (
                    <td key={s.season.year} className={s.season.year === current ? 'cur' : ''}>
                      {show(v)} {t && t !== '=' && <span className="trend">{t}</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <figure className="ybars" aria-label="Med po letech">
        <figcaption className="small muted">Med po letech (kg)</figcaption>
        <div className="ybars-plot">
          {shown.map((s) => (
            <div key={s.season.year} className="ybar-col" title={`${s.season.label}: ${fmt(s.honeyKg)} kg`}>
              <span className="ybar-val">{fmt(s.honeyKg)}</span>
              <span className={'ybar' + (s.season.year === current ? ' cur' : '')} style={{ height: `${(s.honeyKg / maxHoney) * 100}%` }} />
              <span className="ybar-label">{s.season.label}</span>
            </div>
          ))}
        </div>
      </figure>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Roční přehled stanoviště
// ---------------------------------------------------------------------------

export function ApiaryYearScreen({ id }: { id: string }) {
  const startMonth = useSeasonStart();
  const [picked, setPicked] = useState<number | null>(null);
  const data = useQuery(async () => {
    const [apiary, colonies, events, queens] = await Promise.all([read.get('apiaries', id), read.all('colonies'), read.all('events'), read.all('queens')]);
    return { apiary, colonies, events, queens };
  }, [id]);
  if (!data?.apiary || startMonth == null) return null;
  const { apiary, events, queens } = data;
  const apEvents = events.filter((e) => e.apiaryId === id && !e.deletedAt);
  const years = seasonsWithData(apEvents, startMonth, todayISO());
  const year = picked ?? years[0];
  const s = season(year, startMonth);
  // včelstva, která na stanovišti v roce byla: mají tu zápis, nebo tu stojí a žijí
  const colonies = data.colonies
    .filter((c) => !c.deletedAt)
    .filter((c) => apEvents.some((e) => e.colonyIds.includes(c.id) && e.date >= s.from && e.date <= s.to) || (c.apiaryId === id && c.status === 'active'))
    .sort(naturalLabel);
  const rows = colonies
    .map((c) => ({ c, st: yearStats(c, apEvents, queens, s) }))
    .sort((a, b) => b.st.honeyKg - a.st.honeyKg || naturalLabel(a.c, b.c));
  const withHoney = rows.filter((r) => r.st.honeyKg > 0);
  const total = rows.reduce((a, r) => a + r.st.honeyKg, 0);
  const avg = rows.length ? total / rows.length : 0;

  const exportTable = {
    name: `${apiary.name} – rok ${s.label}`,
    head: ['Včelstvo', 'Med kg', 'Medobraní', 'Krmení cukr kg', 'Léčení', 'Celkový spad roztočů', 'Uliček max', 'Rojení', 'Matka rok', 'Linie', 'Stav'],
    rows: rows.map(({ c, st }) => [
      colonyName(c.label),
      st.honeyKg,
      st.harvests.length,
      st.feedSugarKg,
      st.treatments.length,
      st.totalMites,
      st.maxSeams,
      st.swarms,
      st.queens.at(-1)?.year,
      st.queens.at(-1)?.line,
      COLONY_STATUS[st.endStatus],
    ]),
  };

  // porovnání stanoviště po letech
  const history = years
    .slice(0, 5)
    .reverse()
    .map((y) => {
      const ss = season(y, startMonth);
      const cs = data.colonies.filter((c) => apEvents.some((e) => e.colonyIds.includes(c.id) && e.date >= ss.from && e.date <= ss.to));
      const sts = cs.map((c) => yearStats(c, apEvents, queens, ss));
      const honey = sts.reduce((a, x) => a + x.honeyKg, 0);
      return { ss, count: cs.length, honey, avg: cs.length ? honey / cs.length : 0, treatments: sts.reduce((a, x) => a + x.treatments.length, 0) };
    });

  return (
    <Screen title={`Rok ${s.label}`} crumbs={[['Včelnice', ''], [apiary.name, `stanoviste/${id}`]]} backTo={`stanoviste/${id}`}>
      <SeasonPicker years={years} value={year} startMonth={startMonth} onChange={setPicked} />
      <div className="print-only print-head">
        <h1>
          {apiary.name} – rok {s.label}
        </h1>
      </div>
      <div className="info-grid">
        <Stat label="Med celkem" value={`${fmt(total)} kg`} sub={`${withHoney.length} z ${plural(rows.length, 'včelstva', 'včelstev', 'včelstev')}`} />
        <Stat label="Průměr na včelstvo" value={`${fmt(avg)} kg`} sub={plural(rows.length, 'včelstvo', 'včelstva', 'včelstev')} />
      </div>

      {rows.length === 0 ? (
        <Empty>V tomto roce na stanovišti nejsou žádná včelstva.</Empty>
      ) : (
        <Section title="Včelstva podle medu">
          <div className="table-wrap">
            <table className="log compare">
              <thead>
                <tr>
                  <th>Včelstvo</th>
                  <th>Med kg</th>
                  <th>Krmení</th>
                  <th>Léčení</th>
                  <th>Spad celkem</th>
                  <th>Rojení</th>
                  <th>Matka</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ c, st }, i) => (
                  <tr key={c.id}>
                    <th scope="row">
                      <Link to={`vcelstvo/${c.id}/rok?r=${year}`}>{colonyName(c.label)}</Link>
                      {i === 0 && st.honeyKg > 0 && <span className="badge honey"> nejvíc</span>}
                      {c.status !== 'active' && <div className="small muted">{COLONY_STATUS[c.status]}</div>}
                    </th>
                    <td>
                      <strong>{fmt(st.honeyKg)}</strong>
                      {avg > 0 && <div className="small muted">{st.honeyKg >= avg ? '▲' : '▼'} průměr</div>}
                    </td>
                    <td>{feedText(st)}</td>
                    <td>{st.treatments.length || '–'}</td>
                    <td className={(st.maxMitesPerDay ?? 0) >= 10 ? 'alarm' : ''}>{n(st.totalMites, 0)}</td>
                    <td>{st.swarms || (st.swarmCells ? 'matečníky' : '–')}</td>
                    <td>{st.queens.at(-1)?.year ?? '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {rows.length > 0 && <ApiaryVarroa colonies={rows.map((r) => r.c).sort(naturalLabel)} events={apEvents} from={s.from} to={s.to} />}
      {rows.length > 0 && (
        <Section title="Kalendář sezóny">
          <div className="chart-card">
            <SeasonCalendar rows={seasonCalendar(rows.map((r) => r.c).sort(naturalLabel), apEvents, s.from, s.to)} from={s.from} to={s.to} />
            <CalendarLegend />
          </div>
        </Section>
      )}

      {history.length >= 2 && (
        <Section title="Porovnání s předchozími roky">
          <div className="table-wrap">
            <table className="log compare">
              <thead>
                <tr>
                  <th />
                  {history.map((h) => (
                    <th key={h.ss.year} className={h.ss.year === year ? 'cur' : ''}>
                      {h.ss.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(
                  [
                    ['Včelstev', (h: (typeof history)[0]) => h.count, 0],
                    ['Med celkem (kg)', (h: (typeof history)[0]) => h.honey, 1],
                    ['Med na včelstvo (kg)', (h: (typeof history)[0]) => h.avg, 1],
                    ['Léčení celkem', (h: (typeof history)[0]) => h.treatments, 0],
                  ] as const
                ).map(([label, get, digits]) => (
                  <tr key={label}>
                    <th scope="row">{label}</th>
                    {history.map((h, i) => {
                      const t = i > 0 ? trend(get(h), get(history[i - 1])) : '';
                      return (
                        <td key={h.ss.year} className={h.ss.year === year ? 'cur' : ''}>
                          {fmt(get(h), digits)} {t && t !== '=' && <span className="trend">{t}</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      <ExportButtons table={exportTable} />
    </Screen>
  );
}


// ---------------------------------------------------------------------------
// Grafy varroázy
// ---------------------------------------------------------------------------

function VarroaSection({ colonyId, events, from, to }: { colonyId: string; events: ColonyEvent[]; from: string; to: string }) {
  const series = varroaSeries(colonyId, events, from, to);
  if (!series.falls.length && !series.treatments.length) return null;
  const total = series.falls.reduce((a, f) => a + f.mites, 0);
  return (
    <Section title="Varroáza během roku">
      <div className="chart-card">
        <VarroaChart series={series} from={from} to={to} />
        <VarroaLegend />
      </div>
      <p className="small muted">
        Celkový spad za rok: <strong>{plural(total, 'roztoč', 'roztoči', 'roztočů')}</strong> · {plural(series.treatments.length, 'léčení', 'léčení', 'léčení')}.
      </p>
    </Section>
  );
}

export function ApiaryVarroa({ colonies, events, from, to }: { colonies: Colony[]; events: ColonyEvent[]; from: string; to: string }) {
  const all = colonies.map((c) => ({ c, s: varroaSeries(c.id, events, from, to) })).filter((x) => x.s.falls.length || x.s.treatments.length);
  if (!all.length) return null;
  const yMax = niceMax(Math.max(10, ...all.flatMap((x) => x.s.falls.map((f) => f.mites))));
  const totals = all.map((x) => x.s.falls.reduce((a, f) => a + f.mites, 0));
  const worst = Math.max(...totals);
  return (
    <Section title="Varroáza – všechna včelstva">
      <p className="small muted">Všechny grafy mají stejné měřítko (0–{yMax}), takže je hned vidět, které včelstvo je na tom nejhůř.</p>
      <div className="small-multiples">
        {all.map(({ c, s }, i) => (
          <Link key={c.id} to={`vcelstvo/${c.id}/rok`} className="chart-card">
            <div className="sm-title">
              <span>{colonyName(c.label)}</span>
              <span className={totals[i] === worst && worst > 0 ? 'alarm-text' : 'muted'}>{totals[i]}</span>
            </div>
            <VarroaChart series={s} from={from} to={to} yMax={yMax} compact title={`Spad roztočů – včelstvo ${colonyName(c.label)}`} />
          </Link>
        ))}
      </div>
      <VarroaLegend />
    </Section>
  );
}
